'use client';

import { Card, Input, Statistic, Pagination, Typography, TabsProps, Tabs } from 'antd';
import { ChevronLeft, Search } from 'lucide-react';
import Router from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import orderApi from '@apis/order';
import { useDebounce } from '@hooks/useDebounce';
import { userActions } from '@store/actions';
import { EOrderStatus, EPaymentMethod, IOrder } from '@type/order';
import { IResponse } from '@type/response';

import OrderTable from './OrderTable';
import { If } from '@components/condition';

const { Title } = Typography;

interface ISummary {
  status: EOrderStatus;
  total: number;
  count: number;
}

const items: TabsProps['items'] = [
  {
    key: '',
    label: 'Tất cả',
  },
  {
    key: EOrderStatus.PENDING,
    label: 'Chờ xác nhận',
  },
  {
    key: EOrderStatus.CONFIRM,
    label: 'Xác nhận',
  },
  {
    key: EOrderStatus.SHIPPING,
    label: 'Đang giao',
  },
  {
    key: [EOrderStatus.COMPLETED, EOrderStatus.DEBT, EOrderStatus.PAID].join(','),
    label: 'Đã giao',
  },
  {
    key: EOrderStatus.CANCELED,
    label: 'Đơn huỷ',
  },
];

export function OrderHistory() {
  const dispatch = useDispatch();
  const [orders, setOrders] = useState<IResponse<IOrder>>(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<ISummary[]>([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const getOrders = async ({
    page = 1,
    limit = 5,
    filters = {},
  }: {
    page?: number;
    limit?: number;
    filters?: {
      status?: string[];
      paymentMethod?: EPaymentMethod;
      keyword?: string;
    };
  }) => {
    try {
      setLoading(true);
      const { data } = await orderApi.getListOrder({
        limit,
        page,
        filters,
      });
      setOrders(data);
    } catch (error) {
      setOrders(null);
    } finally {
      setLoading(false);
    }
  };

  const getSummary = async () => {
    try {
      const { data } = await orderApi.getSummaryOrder();
      setSummary(data);
    } catch (error) {
      setSummary(null);
    }
  };

  useEffect(() => {
    getSummary();
  }, []);

  useEffect(() => {
    getOrders({
      page: 1,
      limit: 5,
      filters: {
        keyword: debouncedSearch,
        status: statusFilter ? statusFilter?.split(',') : null,
      },
    });
  }, [debouncedSearch, statusFilter]);

  return (
    <div className="p-4 lg:p-10 min-h-[70vh]">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="flex items-center gap-2 text-xl lg:text-2xl font-semibold text-gray-900 mb-2">
          <ChevronLeft
            className="cursor-pointer lg:hidden"
            onClick={() => {
              dispatch(userActions.self.setShowProfileSider(true));
            }}
            size={32}
          />
          QUẢN LÝ ĐƠN HÀNG
        </h1>
        <p className="text-gray-600 text-sm">Quản lý thông tin và theo dõi đơn hàng.</p>
      </div>
      <div className="mb-8 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card
          className="cursor-pointer px-0"
          onClick={() => {
            setStatusFilter('');
          }}
        >
          <Statistic
            title={<span className="font-medium text-slate-700">Tổng đơn hàng</span>}
            value={summary?.reduce((total, item) => total + item.count, 0)}
            prefix={<ShoppingCartOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
        <Card
          className="cursor-pointer"
          onClick={() => {
            setStatusFilter(EOrderStatus?.PENDING);
          }}
        >
          <Statistic
            title={<span className="font-medium text-slate-700">Chờ xử lý</span>}
            value={summary?.find((item) => item.status === EOrderStatus.PENDING)?.count || 0}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#faad14' }}
          />
        </Card>
        <Card
          className="cursor-pointer"
          onClick={() => {
            setStatusFilter(EOrderStatus?.SHIPPING);
          }}
        >
          <Statistic
            title={<span className="font-medium text-slate-700">Đang giao</span>}
            value={summary?.find((item) => item.status === EOrderStatus.SHIPPING)?.count || 0}
            prefix={<TruckOutlined />}
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
        <Card
          className="cursor-pointer"
          onClick={() => {
            setStatusFilter(EOrderStatus?.COMPLETED);
          }}
        >
          <Statistic
            title={<span className="font-medium text-slate-700">Hoàn thành</span>}
            value={
              summary?.find((item) =>
                [EOrderStatus.COMPLETED, EOrderStatus.DEBT, EOrderStatus.PAID].includes(item.status)
              )?.count || 0
            }
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
        <Card
          className="cursor-pointer"
          onClick={() => {
            setStatusFilter(EOrderStatus?.CANCELED);
          }}
        >
          <Statistic
            title={<span className="font-medium text-slate-700">Đã hủy</span>}
            value={summary?.find((item) => item.status === EOrderStatus.CANCELED)?.count || 0}
            prefix={<CloseCircleOutlined />}
            valueStyle={{ color: '#ff4d4f' }}
          />
        </Card>
      </div>
      {/* Orders Table */}
      <div className="flex justify-between align-center flex-wrap gap-4">
        <Title level={4} style={{ margin: 0 }}>
          Danh sách đơn hàng ({orders?.pagination?.totalItems || 0})
        </Title>
        <Input
          className="h-10 !w-full max-w-xs"
          placeholder="Tìm kiếm đơn hàng..."
          prefix={<Search className="text-orange-600 w-5" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      </div>
      <Tabs activeKey={statusFilter} items={items} onChange={setStatusFilter} />
      <OrderTable
        loading={loading}
        data={orders?.data}
        handleClickEdit={(order) => {
          Router.push(`/ho-so/don-hang/${order._id}`);
        }}
      />
      <If
        condition={orders?.data?.length}
        Then={
          <div className="flex justify-center">
            <Pagination
              pageSize={orders?.pagination?.limit || 5}
              total={orders?.pagination?.totalItems}
              current={orders?.pagination?.page}
              onChange={(page, pageSize) => {
                getOrders({
                  limit: pageSize,
                  page,
                });
              }}
            />
          </div>
        }
      />
    </div>
  );
}
